import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8082/api';

function extractTiktokId(url) {
  const m = url.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/);
  return m ? m[1] : null;
}

export default function PromoModal() {
  const navigate = useNavigate();
  const [promo, setPromo] = useState(null);
  const [show, setShow] = useState(false);
  const [visible, setVisible] = useState(false);
  const [tiktokUrls, setTiktokUrls] = useState([]);
  const [thumbs, setThumbs] = useState({});
  const [tiktokPopup, setTiktokPopup] = useState(null);
  const [tiktokPlaying, setTiktokPlaying] = useState(false);
  const [featuredVehicle, setFeaturedVehicle] = useState(null);
  const [slideIndex, setSlideIndex] = useState(0);

  useEffect(() => {
    if (sessionStorage.getItem('promoSeen')) return;
    fetch(`${API_URL}/promo/public`)
      .then(r => r.json())
      .then(data => {
        if (!data.enabled) return;
        setPromo(data);
        const urls = (() => {
          try { return data.tiktokUrls ? JSON.parse(data.tiktokUrls) : []; } catch { return []; }
        })();
        setTiktokUrls(urls);
        urls.forEach(url => {
          fetch(`https://www.tiktok.com/oembed?url=${encodeURIComponent(url)}`)
            .then(r => r.json())
            .then(d => { if (d.thumbnail_url) setThumbs(p => ({ ...p, [url]: d.thumbnail_url })); })
            .catch(() => {});
        });
        if (data.featuredVehicleId) {
          fetch(`${API_URL}/vehicles/public/${data.featuredVehicleId}`)
            .then(r => r.json())
            .then(v => setFeaturedVehicle(v))
            .catch(() => {});
        }
        setTimeout(() => {
          setShow(true);
          requestAnimationFrame(() => setTimeout(() => setVisible(true), 30));
        }, 5000);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!featuredVehicle) return;
    const images = [
      featuredVehicle.mainImageUrl,
      ...(featuredVehicle.imageUrls || []).filter(u => u !== featuredVehicle.mainImageUrl),
    ].filter(Boolean);
    if (images.length <= 1) return;
    setSlideIndex(0);
    const timer = setInterval(() => setSlideIndex(p => (p + 1) % images.length), 4000);
    return () => clearInterval(timer);
  }, [featuredVehicle]);

  const close = () => {
    setVisible(false);
    setTimeout(() => setShow(false), 500);
    sessionStorage.setItem('promoSeen', '1');
  };

  const go = (dest) => {
    close();
    setTimeout(() => navigate(dest), 520);
  };

  if (!show || !promo) return null;

  const kenBurnsStyle = {
    animation: 'kenburns 12s ease-in-out infinite alternate',
  };

  const badge = promo.badgeText || '🔥 OFFRES SPÉCIALES';
  const title = promo.title || 'Nos Meilleures Offres du Moment';
  const subtitle = promo.subtitle || 'Des véhicules d\'exception à prix exceptionnels — disponibles maintenant.';
  const description = promo.description || 'Chez Royal Cars, nous sélectionnons pour vous les plus belles affaires du marché. Profitez de nos offres limitées sur des véhicules contrôlés, garantis et proposés à des prix transparents.';
  const ctaText = promo.ctaText || 'Découvrir nos offres';
  const ctaUrl = promo.ctaUrl || '/vehicles';

  return (
    <>
      <style>{`
        @keyframes kenburns {
          0%   { transform: scale(1)    translate(0%,    0%); }
          25%  { transform: scale(1.04) translate(-1.5%, -0.5%); }
          50%  { transform: scale(1.06) translate(1%,    0.5%); }
          75%  { transform: scale(1.04) translate(0.5%,  1%); }
          100% { transform: scale(1)    translate(0%,    0%); }
        }
      `}</style>

      {/* Backdrop — site visible derrière */}
      <div
        className={`fixed inset-0 z-[9998] transition-all duration-500 ${visible ? 'opacity-100' : 'opacity-0'}`}
        style={{ background: 'rgba(0,0,0,0.55)' }}
        onClick={close}
      />

      {/* Wrapper de centrage — marges visibles sur les côtés */}
      <div
        className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto"
        style={{ padding: '0.75rem 1rem' }}
        onClick={close}
      >
      {/* Popup centré — max 900px, le site s'aperçoit sur les côtés */}
      <div
        className={`bg-white rounded-3xl shadow-2xl w-full relative transition-all duration-500 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
        style={{ maxWidth: '900px', fontFamily: 'system-ui, -apple-system, sans-serif' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Barre du haut */}
        <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-gray-100 shadow-sm rounded-t-3xl">
          <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
            <span className="text-lg font-extrabold tracking-widest uppercase text-black">
              ROYAL <span className="text-yellow-500">CARS</span>
            </span>
            <button onClick={close}
              className="text-gray-400 hover:text-black text-sm font-medium transition flex items-center gap-1.5 px-4 py-2 rounded-full hover:bg-gray-100">
              Fermer <span className="text-base">×</span>
            </button>
          </div>
        </div>

        {/* Véhicule en vedette — EN HAUT, diaporama automatique */}
        {featuredVehicle && (() => {
          const vehicleImages = [featuredVehicle.mainImageUrl, ...(featuredVehicle.imageUrls || [])].filter(Boolean);
          return (
          <div
            className="relative cursor-pointer overflow-hidden"
            style={{ height: '420px', background: '#111' }}
            onClick={() => go(`/vehicle/${featuredVehicle.id}`)}>

            {/* Toutes les images empilées — fade entre elles */}
            {vehicleImages.length > 0 ? vehicleImages.map((img, i) => (
              <img
                key={i}
                src={img}
                alt={`${featuredVehicle.marque} ${featuredVehicle.modele}`}
                className="absolute inset-0 w-full h-full object-cover"
                style={{
                  opacity: i === slideIndex ? 1 : 0,
                  transition: 'opacity 1s ease-in-out',
                  animation: i === slideIndex ? 'kenburns 12s ease-in-out infinite alternate' : 'none',
                }}
              />
            )) : (
              <div className="absolute inset-0 w-full h-full flex items-center justify-center text-7xl bg-gray-900">🚗</div>
            )}

            {/* Gradient sombre */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
            {featuredVehicle.prixPromo && (
              <div className="absolute top-4 left-4 bg-red-500 text-white text-xs font-extrabold px-4 py-2 rounded-full shadow-lg uppercase tracking-widest">PROMO</div>
            )}

            {/* Contenu sur l'image */}
            <div className="relative z-10 p-6 pt-16 md:p-10 md:pt-20 flex flex-col md:flex-row items-end md:items-center justify-between gap-4" style={{ minHeight: '260px' }}>
              <div>
                <p className="text-yellow-400 text-xs font-extrabold uppercase tracking-widest mb-1">Notre véhicule du moment</p>
                <h2 className="text-white text-3xl md:text-4xl font-extrabold mb-1 drop-shadow-lg">
                  {featuredVehicle.marque} {featuredVehicle.modele}
                </h2>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-gray-300 text-sm">{featuredVehicle.dateMiseEnCirculation}</span>
                  {featuredVehicle.kilometrage > 0 && <span className="text-gray-300 text-sm">· {Number(featuredVehicle.kilometrage).toLocaleString('fr-FR')} km</span>}
                  <span className="text-gray-300 text-sm">· {featuredVehicle.carburant}</span>
                </div>
                {featuredVehicle.prix && (
                  <div className="flex items-baseline gap-2 mt-2">
                    {featuredVehicle.prixPromo
                      ? <><span className="text-2xl font-extrabold text-red-400">{Number(featuredVehicle.prixPromo).toLocaleString('fr-FR')} €</span><span className="text-gray-400 text-base line-through">{Number(featuredVehicle.prix).toLocaleString('fr-FR')} €</span></>
                      : <span className="text-2xl font-extrabold text-yellow-400">{Number(featuredVehicle.prix).toLocaleString('fr-FR')} €</span>
                    }
                  </div>
                )}
              </div>
              <button className="flex-shrink-0 flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-black font-extrabold px-7 py-4 rounded-2xl text-base shadow-2xl group-hover:scale-105 transition-all duration-300">
                Voir les détails de ce véhicule
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </div>

            {/* Points de navigation */}
            {vehicleImages.length > 1 && (
              <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                {vehicleImages.map((_, i) => (
                  <button
                    key={i}
                    onClick={e => { e.stopPropagation(); setSlideIndex(i); }}
                    className="rounded-full transition-all duration-300"
                    style={{
                      width: i === slideIndex ? '24px' : '8px',
                      height: '8px',
                      background: i === slideIndex ? '#eab308' : 'rgba(255,255,255,0.5)',
                    }}
                  />
                ))}
              </div>
            )}
          </div>
          );
        })()}

        {/* HERO */}
        <div className="relative overflow-hidden bg-gray-50">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-yellow-500 opacity-[0.07] pointer-events-none" />
          <div className="absolute top-20 right-10 w-[200px] h-[200px] rounded-full bg-yellow-500 opacity-[0.05] pointer-events-none" />

          <div className="max-w-6xl mx-auto px-6 pt-14 pb-10">

            {/* Badge */}
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              <span className="inline-flex items-center gap-2 bg-yellow-500 text-black text-xs font-extrabold uppercase tracking-widest px-5 py-2 rounded-full shadow-md">
                <span className="w-1.5 h-1.5 bg-black rounded-full" />
                {badge}
              </span>
              {promo.expirationText && (
                <span className="text-gray-500 text-sm font-medium border border-gray-200 px-3 py-1.5 rounded-full">
                  ⏰ {promo.expirationText}
                </span>
              )}
            </div>

            {/* Titre */}
            <h1 className="text-5xl md:text-7xl font-extrabold leading-none tracking-tight mb-5">
              {title.split(' ').map((word, i) => (
                <span key={i} className={i % 4 === 2 ? 'text-yellow-500' : 'text-black'}>{word} </span>
              ))}
            </h1>

            {/* Ligne déco */}
            <div className="w-24 h-1.5 bg-yellow-500 rounded-full mb-6" />

            {/* Sous-titre */}
            <p className="text-gray-700 text-xl md:text-2xl font-medium mb-4 max-w-2xl">{subtitle}</p>

            {/* Description */}
            <p className="text-gray-500 text-base leading-relaxed max-w-xl mb-8">{description}</p>

            {/* CTA */}
            <div className="flex flex-wrap items-center gap-4">
              <button
                onClick={() => go(ctaUrl)}
                className="group inline-flex items-center gap-3 bg-black text-white px-8 py-4 rounded-2xl font-extrabold text-lg hover:bg-yellow-500 hover:text-black transition-all duration-300 shadow-xl hover:shadow-yellow-500/30 hover:scale-105">
                {ctaText}
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
              <a href="tel:+33647474068"
                className="inline-flex items-center gap-2 border-2 border-gray-200 text-gray-700 px-6 py-4 rounded-2xl font-bold text-base hover:border-yellow-500 hover:text-yellow-600 transition">
                📞 Nous appeler
              </a>
            </div>
          </div>
        </div>

        {/* 3 points forts */}
        <div className="bg-gray-50 border-t border-b border-gray-100 py-10">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: '✅', title: 'Véhicules contrôlés', desc: 'Chaque véhicule est inspecté et certifié avant d\'être mis en vente.' },
                { icon: '💰', title: 'Prix transparents', desc: 'Pas de frais cachés. Le prix affiché est le prix final, HT et TTC.' },
                { icon: '🚀', title: 'Livraison rapide', desc: 'Livraison possible partout en France, en Algérie et en Tunisie. Votre véhicule arrive chez vous.' },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-200 p-6 flex gap-4 items-start shadow-sm hover:shadow-md hover:border-yellow-300 transition-all">
                  <span className="text-3xl flex-shrink-0">{item.icon}</span>
                  <div>
                    <h3 className="font-bold text-black text-base mb-1">{item.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Section TikTok */}
        {tiktokUrls.length > 0 && (
          <div className="py-14">
            <div className="max-w-6xl mx-auto px-6">
              <div className="flex items-center gap-4 mb-8">
                <div className="flex-1 h-px bg-gray-200" />
                <div className="flex items-center gap-2 flex-shrink-0">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-black" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.78a4.85 4.85 0 01-1.01-.09z"/>
                  </svg>
                  <span className="text-black font-bold text-sm uppercase tracking-widest">Nos vidéos TikTok</span>
                </div>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
              <p className="text-center text-gray-500 text-base mb-8 max-w-xl mx-auto">
                Abonnez-vous à notre TikTok pour voir en avant-première toutes nos nouvelles arrivées !
              </p>
              <div className="flex gap-5 overflow-x-auto pb-3 justify-start md:justify-center"
                style={{ scrollbarWidth: 'thin', scrollbarColor: '#eab308 #f3f4f6' }}>
                {tiktokUrls.map((url, i) => {
                  const tid = extractTiktokId(url);
                  const thumb = thumbs[url];
                  return (
                    <div key={i}
                      className="flex-shrink-0 relative cursor-pointer group rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-gray-200 hover:border-yellow-500 hover:-translate-y-2"
                      style={{ width: '240px', height: '420px', background: '#111' }}
                      onClick={() => { setTiktokPopup({ url, tid }); setTiktokPlaying(false); }}>
                      {thumb ? (
                        <img src={thumb} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-90" />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-gray-900">
                          <svg viewBox="0 0 24 24" className="w-14 h-14 text-white opacity-40" fill="currentColor">
                            <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.78a4.85 4.85 0 01-1.01-.09z"/>
                          </svg>
                          <span className="text-gray-500 text-sm font-bold">TikTok</span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent group-hover:from-black/50 transition-all" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-xl group-hover:bg-yellow-500 group-hover:scale-110 transition-all duration-300">
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-black ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        </div>
                      </div>
                      <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                        <span className="bg-yellow-500 text-black text-xs font-extrabold px-4 py-1.5 rounded-full shadow">TikTok</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Bande jaune bas */}
        <div className="bg-yellow-500 py-14">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div>
                <p className="text-black/50 text-xs font-bold uppercase tracking-widest mb-2">Royal Cars · Achat · Vente · Reprise</p>
                <h2 className="text-black font-extrabold text-3xl md:text-4xl leading-tight mb-3">
                  Prêt à trouver<br />votre véhicule idéal ?
                </h2>
                <p className="text-black/70 text-base font-medium max-w-md">
                  Plus de <strong>100 véhicules</strong> disponibles, des prix transparents et une équipe à votre écoute.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                <button
                  onClick={() => go(ctaUrl)}
                  className="group flex items-center justify-center gap-2 bg-black text-white px-8 py-4 rounded-2xl font-extrabold text-base hover:bg-gray-900 transition-all hover:scale-105 shadow-xl">
                  {ctaText}
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
                <button onClick={close}
                  className="flex items-center justify-center px-8 py-4 rounded-2xl font-bold text-black/60 border-2 border-black/20 hover:border-black hover:text-black transition text-base">
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
      </div>

      {/* Popup TikTok */}
      {tiktokPopup && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.96)', backdropFilter: 'blur(16px)' }}
          onClick={() => { setTiktokPopup(null); setTiktokPlaying(false); }}>
          <div className="relative flex flex-col items-center w-full max-w-xs" onClick={e => e.stopPropagation()}>
            <button onClick={() => { setTiktokPopup(null); setTiktokPlaying(false); }}
              className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 border border-white/20 text-white font-bold flex items-center justify-center hover:bg-yellow-500 hover:text-black transition text-xl">×</button>
            <div className="w-full rounded-3xl overflow-hidden shadow-2xl bg-gray-900" style={{ aspectRatio: '9/16', maxHeight: '75vh' }}>
              {tiktokPlaying ? (
                <iframe src={`https://www.tiktok.com/embed/v2/${tiktokPopup.tid}`} title="TikTok"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen className="w-full h-full" />
              ) : (
                <>
                  {thumbs[tiktokPopup.url] ? (
                    <img src={thumbs[tiktokPopup.url]} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-900">
                      <svg viewBox="0 0 24 24" className="w-16 h-16 text-white opacity-30" fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.78a4.85 4.85 0 01-1.01-.09z"/>
                      </svg>
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center" onClick={() => setTiktokPlaying(true)}>
                    <div className="w-20 h-20 rounded-full bg-white/95 flex items-center justify-center shadow-2xl hover:bg-yellow-500 transition cursor-pointer hover:scale-110 duration-300">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-9 h-9 text-black ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
